import '@styles/index.scss'
import { NextPage } from 'next'
import type { AppProps } from 'next/app'
import Head from 'next/head'
import Router from 'next/router'
import NProgress from 'nprogress'
import * as React from 'react'
import { useEffect, useState } from 'react'
import Layout from '@components/Layout'
import Notifications from '@components/Notifications'
import { RouterReady } from '@components/RouterReady'
import { getLayoutConfig } from '../config'
import AppContextProvider from '../providers/AppContext'
import { BookingFormStoreProvider } from '../providers/BookingFormStoreProvider'
import IntlProvider from '../providers/IntlProvider'

const setupProgressBar = () => {
  Router.events.on('routeChangeStart', () => NProgress.start())
  Router.events.on('routeChangeComplete', () => NProgress.done())
  Router.events.on('routeChangeError', () => NProgress.done())
}

const AppComponentWithLayoutAndProviders = ({ Component, pageProps }: AppProps) => {
  // Ensuring a consistent state during hydration
  const [isHydrated, setIsHydrated] = useState(false)

  const config = React.useMemo(() => getLayoutConfig(), [])

  useEffect(() => {
    setIsHydrated(true)
    setupProgressBar()
  }, [])

  if (!isHydrated) {
    return null
  }

  return (
    <>
      <RouterReady>
        <IntlProvider>
          <AppContextProvider>
            <Head>
              <title>{config?.title ?? 'SpaBooking s.r.o.'}</title>
              <meta charSet="utf-8" />
              <meta
                content="width=device-width, initial-scale=1, shrink-to-fit=no"
                name="viewport"
              />
              <link
                href={`/favicon/${config.favicon}/apple-touch-icon.png`}
                rel="apple-touch-icon"
                sizes="180x180"
              />
              <link
                href={`/favicon/${config.favicon}/favicon-32x32.png`}
                rel="icon"
                sizes="32x32"
                type="image/png"
              />
              <link
                href={`/favicon/${config.favicon}/favicon-16x16.png`}
                rel="icon"
                sizes="16x16"
                type="image/png"
              />
              <link
                href={`/favicon/${config.favicon}/favicon.ico`}
                rel="shortcut icon"
                sizes="any"
              />
              <link href={`/favicon/${config.favicon}/site.webmanifest`} rel="manifest" />
            </Head>
            <Layout>
              <Component {...pageProps} />
            </Layout>
          </AppContextProvider>
          <Notifications />
        </IntlProvider>
      </RouterReady>
    </>
  )
}

const MyApp: NextPage<AppProps> = ({ Component, pageProps }: AppProps) => {
  return (
    <BookingFormStoreProvider>
      <AppComponentWithLayoutAndProviders Component={Component} {...pageProps} />
    </BookingFormStoreProvider>
  )
}

export default MyApp
